import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Internal } from '@gleamer/types';
import { isEqual } from 'lodash';

type Tool = Internal.Tool;

type NextObs = {
  code: string;
  status: string;
  index: number;
};

type Control = {
  title: string;
  const: string;
};

export type ToolsState = {
  nextObs: {
    code: string;
    status: string;
    index: number;
  };
  tool: Tool | null;
  currentControl: Control | null;
  availableControls: Control[];
};

const initialState: ToolsState = {
  nextObs: {
    code: '',
    status: '',
    index: 0,
  },
  tool: null,
  currentControl: null,
  availableControls: [],
};

/*
 * Every reducer leaves the state untouched when the action would not change it. The viewer re-dispatches what it
 * already holds (effects that select the pending observation or its tool, the tools middleware after each label pass
 * action): when such a dispatch still produced a new object, every subscriber re-rendered, their effects dispatched
 * again, and React stopped the chain with error #185 (Maximum update depth exceeded), blanking the viewer.
 */
const toolsSlice = createSlice({
  name: 'tools',
  initialState,
  reducers: {
    setNextObs: (state, action: PayloadAction<NextObs>) => {
      if (isEqual(state.nextObs, action.payload)) {
        return;
      }
      state.nextObs = action.payload;
    },

    setTool: (state, action: PayloadAction<Tool | null>) => {
      if (isEqual(state.tool, action.payload)) {
        return;
      }
      state.tool = action.payload;
    },

    setControls: (state, action: PayloadAction<Control[]>) => {
      const currentControl = action.payload?.length > 0 ? action.payload[0] : null;
      if (
        isEqual(state.availableControls, action.payload) &&
        isEqual(state.currentControl, currentControl)
      ) {
        return;
      }
      state.availableControls = action.payload;
      state.currentControl = currentControl;
    },

    setNextControl: state => {
      const { availableControls, currentControl } = state;
      if (!availableControls) {
        return;
      }

      if (!currentControl) {
        state.currentControl = availableControls[0];
      } else {
        const currentIndex = availableControls.findIndex(
          control => control.const === currentControl.const
        );
        state.currentControl = availableControls[(currentIndex + 1) % availableControls.length];
      }
    },

    clear: state => {
      if (isEqual(state, initialState)) {
        return;
      }
      state.nextObs = {
        code: '',
        status: '',
        index: 0,
      };
      state.tool = null;
      state.currentControl = null;
      state.availableControls = [];
    },
  },
});

export const actions = toolsSlice.actions;
export const reducer = toolsSlice.reducer;
